import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const CatastrophicInjury = () => {
  return (
    <div data-testid="catastrophic-injury-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Catastrophic Injury Cases
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            Serious injuries change lives forever. When you or a loved one has suffered a traumatic brain injury, 
            spinal cord damage, severe burns, or other life-altering harm, you need advocates who understand the 
            long-term impact and fight for full compensation.
          </p>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            How We Help
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>In-hospital consultations available upon request for those unable to travel</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Collaboration with medical experts to document the full extent and future impact of injuries</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Life care planning to project future medical needs and associated costs</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Identification of all available insurance coverage, including excess and umbrella policies</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Thorough investigation and evidence preservation from the earliest stages</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Pursuit of compensation for medical bills, lost income, pain, suffering, and diminished quality of life</span>
            </p>
          </div>
        </div>
      </section>

      {/* What to Do Next */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            What to Do Next
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Focus on Medical Treatment
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Your health and recovery are the top priority. Follow all medical advice and attend all appointments. 
                Complete documentation of treatment is essential for your case.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Preserve Evidence Early
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Contact us as soon as possible. Critical evidence can be lost or destroyed if not preserved immediately. 
                We act quickly to protect your rights.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Understand Your Rights
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Catastrophic injuries often involve complex legal and insurance issues. We explain your options clearly 
                and guide you through every step of the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-12 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg">
            Serving clients primarily in <strong>North Carolina</strong> and <strong>South Carolina</strong>. 
            We can also assist with catastrophic injury cases in <strong>Tennessee</strong> where applicable.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-1">
                What qualifies as a catastrophic injury?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Catastrophic injuries are severe, life-altering injuries that result in permanent disability or long-term impairment. 
                Examples include traumatic brain injuries, spinal cord injuries, severe burns, amputations, and injuries from 
                drunk driving or motorcycle accidents.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-2">
                Can you meet with us if our loved one is still in the hospital?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Yes. We offer in-hospital consultations upon request for those unable to travel to our office. 
                We understand that catastrophic injuries require immediate attention, and we're here to help whenever you need us.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-3">
                How is compensation calculated in catastrophic injury cases?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                We work with medical experts and economists to project lifetime medical costs, lost earning capacity, 
                necessary accommodations, and ongoing care needs. We also pursue compensation for pain, suffering, and 
                diminished quality of life.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-4">
                What if the at-fault party doesn't have enough insurance?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                We thoroughly investigate all potential sources of recovery, including excess insurance, umbrella policies, 
                and other liable parties. Our goal is to identify every available dollar to maximize your compensation.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-6 tracking-tight">
            We're Here to Help
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Contact us for a free consultation about your catastrophic injury case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="border-2 border-primary text-primary px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CatastrophicInjury;